import {Button, TextField} from "@mui/material";
import EmailInput from "auth/EmailInput";
import PasswordInput from "auth/PasswordInput";
import PhoneInput from "auth/PhoneInput";
import {useRegisterMutation} from "auth/api";
import {validatePassword} from "auth/utils/validators";
import {useAuth} from "hooks/useAuth";
import useForm, {type FieldConfig} from "hooks/useForm";
import type {FC} from "react";
import {handleMutation} from "services/http/utils";
import {
  noopValidator,
  validateEmail,
  validatePhone,
  validateRequiredString,
} from "utils/validators";

const SignUpForm: FC = () => {
  const {setUser} = useAuth();
  const [signUp, {loading}] = useRegisterMutation();

  const initialFields: Record<string, FieldConfig<any>> = {
    company_name: ["", validateRequiredString],
    full_name: ["", validateRequiredString],
    email: ["", v => validateEmail(v, true)],
    phone: ["", v => validatePhone(v, true)],
    password: ["", validatePassword],
    confirmPassword: ["", noopValidator],
  };

  const {fields, handleSubmit, setFieldError} = useForm({
    fields: initialFields,
    onSubmit: async values => {
      if (values.password !== values.confirmPassword) {
        setFieldError("confirmPassword", "Passwords do not match");
        return;
      }

      const payload = {
        company_name: values.company_name,
        full_name: values.full_name,
        email: values.email,
        phone: values.phone,
        password: values.password,
      };

      await handleMutation(
        signUp,
        payload,
        "User reset request sent",
        undefined,
        {
          onSuccess: user => setUser(user),
        },
      );
    },
  });

  return (
    <form onSubmit={handleSubmit} style={{width: "100%", maxWidth: 400}}>
      <TextField
        label="Company Name"
        value={fields.company_name.value}
        onChange={e => fields.company_name.onChange(e.target.value)}
        onBlur={fields.company_name.onBlur}
        helperText={fields.company_name.error}
        error={!!fields.company_name.error}
        fullWidth
        margin="normal"
      />

      <TextField
        label="Full Name"
        value={fields.full_name.value}
        onChange={e => fields.full_name.onChange(e.target.value)}
        onBlur={fields.full_name.onBlur}
        helperText={fields.full_name.error}
        error={!!fields.full_name.error}
        fullWidth
        margin="normal"
      />

      <EmailInput {...fields.email} />

      <PhoneInput {...fields.phone} />

      <PasswordInput label="Password" {...fields.password} />

      <PasswordInput label="Confirm Password" {...fields.confirmPassword} />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        loading={loading}
        sx={{mt: 2}}>
        Sign Up
      </Button>
    </form>
  );
};

export default SignUpForm;
